import type { ApiListingLocationType } from '#/api/types/ApiListingLocationType';
import type { ApiISODateTimeString } from '#/api/types/ApiTypedDate';
import { Box } from '#/components/Box/Box';
import { Tooltip } from '#/components/Tooltip/Tooltip';
import { useI18n } from '#/i18n/hooks/useI18n';
import { ListingTypeLabel } from '#/modules/listing/components/ListingTypeLabel/ListingTypeLabel';
import { SearchHitInfoBoxListingLabels } from '#/modules/search/components/Hit/Info/ListingLabels/SearchHitInfoBoxListingLabels';
import {
  SearchHitInfoHeader,
  SearchHitInfoHeaderHeading,
  SearchHitInfoHeaderOrgName,
  SearchHitInfoHolder,
} from '#/modules/search/components/Hit/Info/SearchHitInfo.styled';
import type { EmploymentType } from '#/utils/constants/general/employmentTypes';

import { SearchJobHitFooter } from './Footer/SearchJobHitFooter';

type Props = {
  application: {
    status: 'ACTIVE' | 'INACTIVE';
    created: ApiISODateTimeString;
  } | null;
  jobType: EmploymentType[];
  isPostedAnonymously: boolean;
  location: string;
  locationType: ApiListingLocationType;
  name: string;
  orgName: string | null;
  orgType: string | null;
  salaryFormatted: string | null;
  showLabel?: boolean;
  published: number | null;
};

export function SearchJobHitInfo({
  application,
  jobType,
  isPostedAnonymously,
  location,
  locationType,
  name,
  orgName,
  orgType,
  salaryFormatted,
  showLabel,
  published,
}: Props) {
  const { i18n } = useI18n();

  const type = 'JOB';

  return (
    <SearchHitInfoHolder>
      {showLabel && (
        <div style={{ marginBottom: 14 }}>
          <ListingTypeLabel listingType={type} orgType={null} />
        </div>
      )}

      <SearchHitInfoHeader>
        <SearchHitInfoHeaderHeading>
          <div>
            <span data-qa-id="search-result-link">{name.slice(0, 120)}</span>
            {name.length > 120 && <>&hellip;</>}
          </div>

          {isPostedAnonymously && (
            <Box display="inline-block" ml="4px" verticalAlign="middle">
              <Tooltip
                content={
                  <Box padding="6px">
                    {i18n(
                      `The hiring organization has chosen to publish this listing anonymously to maintain confidentiality during the hiring process. They have provided Idealist with all of their organization’s details and currently meet our posting guidelines.`,
                    )}
                  </Box>
                }
                tooltipMinimumHeight={320}
                tooltipWidth="250px"
                placement="bottom-right"
                size="small"
                variant="brand-blue"
              />
            </Box>
          )}
        </SearchHitInfoHeaderHeading>

        {(orgType === 'CORPORATION' || orgName) && (
          <SearchHitInfoHeaderOrgName>
            <Box display="flex" alignItems="center" flexWrap="wrap">
              {orgName}
            </Box>
          </SearchHitInfoHeaderOrgName>
        )}
      </SearchHitInfoHeader>

      <SearchHitInfoBoxListingLabels
        listingType={type}
        location={location}
        jobTypes={jobType}
        locationType={locationType}
        salaryFormatted={salaryFormatted}
        pillItems={[]}
        endDateTime={null}
        startDateTime={null}
        timezone={null}
      />

      <SearchJobHitFooter application={application} published={published} />
    </SearchHitInfoHolder>
  );
}
